import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Form, FormikProvider, useFormik } from 'formik';
import { Card, Button, CircularProgress, useTheme } from '@mui/material';
import { AlertMessage } from '@eposnow/ui-core';
import ActionsContainer from '../../components/ActionsContainer';
import PageHeader from '../../components/pageHeaderComponents/PageHeader';
import PersonalInformationForm from './PersonalInformationForm';
import validationSchema from './validationSchema';
import { StepContext } from '../../context/StepContext';
import { PersonalInfo } from '../../types';
import { API_V1_HOSTNAME_KEY } from '../../localStorageKeys';
import { ApiContext, ErrorObject } from '../../context/ApiContext';
import Loader from '../../components/Loader';
import { checkAndFormatCountry, formatDateWithLeadingZeroes } from '../../helpers/helpers';
import { UserContext } from '../../context/UserContext';
import useCountries from '../../helpers/useCountries';
import usePageTitle from '../../helperHooks/usePageTitle';
import forbiddenNames from './forbiddenNamesList';

const PersonalInformation = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { goToNextStep } = useContext(StepContext);
  const { apiFetch } = useContext(ApiContext);
  const { locale } = useContext(UserContext);
  const { countries } = useCountries();
  const [errorMessage, setErrorMessage] = useState('');
  const [includeEmailField, setIncludeEmailField] = useState(false);

  usePageTitle('nav.personalInformation');

  const { data, isLoading } = useQuery<PersonalInfo>(
    ['personalInformation'],
    () => apiFetch(`${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/personal-info`),
    {
      onSuccess(response: PersonalInfo) {
        setIncludeEmailField(!response?.emailAddress);
      },
      onError(error: ErrorObject) {
        if (error?.code === 401) return;
        Sentry.captureException(new Error('Error fetching personal information'), {
          extra: { error: JSON.stringify(error) },
        });
      },
    }
  );

  const { isLoading: isUpdatingPersonalInfo, mutate: updatePersonalInfo } = useMutation(
    (newPersonalInfo: PersonalInfo) =>
      apiFetch(
        `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/personal-info`,
        newPersonalInfo,
        'PUT'
      ),
    {
      onSuccess: () => {
        goToNextStep();
      },
      onError(error: ErrorObject) {
        if (error?.code === 401) return;
        Sentry.captureException(new Error('Error updating personal information'), {
          extra: { error: JSON.stringify(error) },
        });
        setErrorMessage(t('errors.errorSavingData'));
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      title: data?.title || '',
      firstName: data?.firstName || '',
      lastName: data?.lastName || '',
      role: data?.role || '',
      dateOfBirth: data?.dateOfBirth || '',
      emailAddress: data?.emailAddress || '',
      phoneNumber: data?.phoneNumber || '',
      addressLine1: data?.addressLine1 || '',
      town: data?.town || '',
      county: data?.county || '',
      postCode: data?.postCode || '',
      country: data?.country || '',
    },
    validationSchema: validationSchema(includeEmailField),
    enableReinitialize: true,
    onSubmit: (values) => {
      if (
        forbiddenNames?.includes(`${values?.firstName} ${values?.lastName}`) ||
        forbiddenNames?.includes(`${values?.title} ${values?.firstName} ${values?.lastName}`)
      ) {
        setErrorMessage(t('validation.noForbiddenName'));
        return;
      }

      // To format date to be yyyy-mm-dd (format expected by the API)
      const dateOfBirth = new Date(values?.dateOfBirth);

      updatePersonalInfo({
        ...data,
        ...values,
        locale,
        dateOfBirth: formatDateWithLeadingZeroes(dateOfBirth),
        country: checkAndFormatCountry(values.country, countries),
      });
    },
  });

  return (
    <>
      <PageHeader />

      {isLoading ? (
        <Loader ariaBusy={isLoading} />
      ) : (
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            {errorMessage && (
              <AlertMessage
                type="error"
                styles={{ marginBottom: 2 }}
                action={() => setErrorMessage('')}
                iconAlignment="baseline"
                locale={locale}
                theme={theme}
              >
                {errorMessage}
              </AlertMessage>
            )}

            <Card
              variant="outlined"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '1.5rem',
                marginBottom: '3rem',
              }}
            >
              <PersonalInformationForm formik={formik} includeEmailField={includeEmailField} />
            </Card>
            <ActionsContainer>
              <Button
                variant="outlined"
                sx={{
                  margin: '0px',
                  flexGrow: '0',
                  height: '48px',
                }}
                onClick={() => {
                  navigate('/');
                }}
              >
                {t('actions.previous')}
              </Button>
              <Button
                variant="contained"
                disabled={isUpdatingPersonalInfo}
                type="submit"
                sx={{
                  margin: '0px',
                  flexGrow: '0',
                  height: '48px',
                }}
              >
                {isUpdatingPersonalInfo ? (
                  <>
                    <CircularProgress color="inherit" size="20px" sx={{ marginRight: '8px' }} />
                    {t('actions.saving')}
                  </>
                ) : (
                  t('actions.next')
                )}
              </Button>
            </ActionsContainer>
          </Form>
        </FormikProvider>
      )}
    </>
  );
};

export default PersonalInformation;
